import { useState, useEffect } from "react";
import {
  Typography, Card, Button, Select, Space, message, Popconfirm,
  Form, Input, DatePicker, Row, Col, Divider, Tag, Table, Alert,
  Tabs, Modal,
} from "antd";
import { PlusOutlined, DeleteOutlined, InboxOutlined, SaveOutlined, EditOutlined, DownloadOutlined, FileExcelOutlined, WarningOutlined } from "@ant-design/icons";
import Upload from "antd/es/upload";
import dayjs from "dayjs";
import {
  getRepertoryYears, createRepertoryEntry, updateRepertoryEntry, deleteRepertoryEntry,
  importRepertoryIndex, searchRepertoryIndex, getRepertoryActTypes,
  exportRepertoryIndex, getRepertoryGaps,
} from "../../api/portalService";

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;
const { TabPane } = Tabs;

// ──────────────────────────────────────────────────────────────────────────────
// Edit Modal
// ──────────────────────────────────────────────────────────────────────────────
const EditModal = ({ entry, actTypes, open, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry && open) {
      form.setFieldsValue({
        year: entry.year,
        repertoryNumber: entry.repertoryNumber,
        date: entry.date ? dayjs(entry.date) : null,
        party1: entry.party1,
        party2: entry.party2 || '',
        actType: entry.actType,
        docProtRef: entry.docProtRef || '',
      });
    }
  }, [entry, open, form]);

  const onFinish = async (values) => {
    setSaving(true);
    try {
      await updateRepertoryEntry(entry.id, {
        ...values,
        date: values.date ? values.date.toISOString() : undefined,
      });
      message.success('Registro actualizado');
      onSaved();
      onClose();
    } catch { message.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title={`Editar repertorio N° ${entry?.repertoryNumber} (${entry?.year})`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={12}>
          <Col xs={8} sm={4}>
            <Form.Item label="Año" name="year" rules={[{ required: true }]}>
              <Select>
                {[2021,2022,2023,2024,2025,2026,2027].map(y => <Option key={y} value={y}>{y}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={8} sm={4}>
            <Form.Item label="N° Rep." name="repertoryNumber" rules={[{ required: true }]}>
              <Input type="number" min={1} />
            </Form.Item>
          </Col>
          <Col xs={8} sm={6}>
            <Form.Item label="Fecha" name="date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={10}>
            <Form.Item label="Tipo de acto" name="actType" rules={[{ required: true }]}>
              <Select showSearch allowClear
                filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
                {actTypes.map(t => <Option key={t} value={t}>{t}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Parte 1" name="party1" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Parte 2 (opcional)" name="party2">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Doc. Prot." name="docProtRef">
              <Input placeholder="Ej: P63 2" />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}
              style={{ background: '#1d3557', borderColor: '#1d3557' }}>
              Guardar cambios
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Recent entries table (live view)
// ──────────────────────────────────────────────────────────────────────────────
const RecentTable = ({ refresh, actTypes }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [years, setYears] = useState([]);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  const load = (p = 1) => {
    setLoading(true);
    const params = { page: p, limit: PAGE_SIZE };
    if (filterYear) params.year = filterYear;
    searchRepertoryIndex(params)
      .then(({ data: d }) => { setData(d.results || []); setTotal(d.totalResults || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getRepertoryYears().then(({ data: d }) => setYears(d.years || [])).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); load(1); }, [refresh, filterYear]);

  const handleDelete = async (id) => {
    try {
      await deleteRepertoryEntry(id);
      message.success('Registro eliminado');
      load(page);
    } catch { message.error('Error al eliminar'); }
  };

  const columns = [
    {
      title: 'Rep.', dataIndex: 'repertoryNumber', width: 75,
      sorter: (a, b) => a.repertoryNumber - b.repertoryNumber,
      defaultSortOrder: 'descend',
      render: v => <Text strong style={{ color: '#1d3557' }}>{v}</Text>,
    },
    {
      title: 'Año', dataIndex: 'year', width: 70,
      sorter: (a, b) => a.year - b.year,
    },
    {
      title: 'Fecha', dataIndex: 'date', width: 110,
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: v => v ? new Date(v).toLocaleDateString('es-CL') : '—',
    },
    {
      title: 'Otorgantes', key: 'parties',
      sorter: (a, b) => (a.party1 || '').localeCompare(b.party1 || ''),
      render: (_, r) => (
        <div>
          <Text style={{ fontSize: 12, fontWeight: 500 }}>{r.party1}</Text>
          {r.party2 && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
              / {r.party2}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Acto', dataIndex: 'actType', width: 170,
      sorter: (a, b) => (a.actType || '').localeCompare(b.actType || ''),
      render: v => v ? <Tag style={{ fontSize: 10, whiteSpace: 'normal', lineHeight: 1.3 }}>{v}</Tag> : '—',
    },
    {
      title: '', key: 'actions', width: 80,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(record)} />
          <Popconfirm
            title="¿Eliminar este registro?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí" cancelText="No" okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{total} registros</Text>
        <Select
          placeholder="Filtrar por año"
          value={filterYear}
          onChange={setFilterYear}
          allowClear
          style={{ width: 130 }}
          size="small"
        >
          {years.map(y => <Option key={y} value={y}>{y}</Option>)}
        </Select>
      </div>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 600 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: t => `${t} registros`,
          onChange: (p) => { setPage(p); load(p); },
        }}
      />

      {editing && (
        <EditModal
          entry={editing}
          actTypes={actTypes}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Manual entry form
// ──────────────────────────────────────────────────────────────────────────────
const ManualEntryForm = ({ actTypes, onCreated }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const onFinish = async (values) => {
    setSaving(true);
    try {
      await createRepertoryEntry({
        ...values,
        date: values.date ? values.date.toISOString() : undefined,
        party2: values.party2 || '',
        docProtRef: values.docProtRef || '',
      });
      message.success(`Repertorio N° ${values.repertoryNumber} (${values.year}) agregado`);
      form.resetFields();
      onCreated?.();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Row gutter={[12, 0]}>
        <Col xs={8} sm={4}>
          <Form.Item label="Año" name="year" rules={[{ required: true, message: 'Requerido' }]}>
            <Select placeholder="Año">
              {[2026,2025,2024,2023,2022,2021].map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={8} sm={4}>
          <Form.Item label="N° Repertorio" name="repertoryNumber" rules={[{ required: true, message: 'Requerido' }]}>
            <Input type="number" placeholder="278" min={1} />
          </Form.Item>
        </Col>
        <Col xs={8} sm={6}>
          <Form.Item label="Fecha" name="date" rules={[{ required: true, message: 'Requerida' }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="DD/MM/AAAA" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={10}>
          <Form.Item label="Tipo de acto" name="actType" rules={[{ required: true, message: 'Requerido' }]}>
            <Select showSearch allowClear placeholder="Selecciona o escribe..."
              filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
              {actTypes.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Otorgante / Parte 1" name="party1" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Ej: GONZALEZ SOTO, JUAN CARLOS" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Otorgante / Parte 2 (opcional)" name="party2">
            <Input placeholder="Ej: MUÑOZ VERA, MARIA ELENA" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label="Doc. Prot. (opcional)" name="docProtRef">
            <Input placeholder="Ej: P63 2" />
          </Form.Item>
        </Col>
      </Row>
      <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}
        style={{ background: '#1d3557', borderColor: '#1d3557' }}>
        Agregar repertorio
      </Button>
    </Form>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// CSV template download
// ──────────────────────────────────────────────────────────────────────────────
const downloadCsvTemplate = () => {
  const rows = [
    'PARTE_1;PARTE_2;ACTO;N_REPERTORIO;FECHA(DD-MM-AAAA);DOC_PROT',
    'GONZALEZ SOTO, JUAN CARLOS;MUÑOZ VERA, MARIA ELENA;COMPRAVENTA;278;15-03-2025;P63 2',
    'TORRES REYES, ANA MARIA;;DECLARACION JURADA;279;16-03-2025;',
  ].join('\n');
  const blob = new Blob(['﻿' + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'plantilla_repertorio.csv';
  a.click(); URL.revokeObjectURL(url);
};

// ──────────────────────────────────────────────────────────────────────────────
// TSV / CSV parser helper (auto-detecta delimitador: tab o punto y coma)
// ──────────────────────────────────────────────────────────────────────────────
const parseRow = (line, year, delimiter = '\t') => {
  const parts = line.split(delimiter);
  if (parts.length < 3) return null;

  let party1, party2, actType, repNum, dateStr, docProtRef;

  if (parts.length >= 6) {
    // Nuevo formato CSV: PARTE_1;PARTE_2;ACTO;N_REPERTORIO;FECHA;DOC_PROT
    party1      = (parts[0] || '').trim();
    party2      = (parts[1] || '').trim();
    actType     = (parts[2] || '').trim().toUpperCase();
    repNum      = parseInt((parts[3] || '').trim(), 10);
    dateStr     = (parts[4] || '').trim();
    docProtRef  = (parts[5] || '').trim();
  } else {
    // Formato TSV legacy: OTORGANTES;ACTO;N_REP;FECHA;DOC_PROT
    const rawOtorgantes = (parts[0] || '').trim();
    actType     = (parts[1] || '').trim().toUpperCase();
    repNum      = parseInt((parts[2] || '').trim(), 10);
    dateStr     = (parts[3] || '').trim();
    docProtRef  = (parts[4] || '').trim();
    const si    = rawOtorgantes.indexOf('/');
    party1      = si > -1 ? rawOtorgantes.substring(0, si).trim() : rawOtorgantes;
    party2      = si > -1 ? rawOtorgantes.substring(si + 1).trim() : '';
  }

  if (!party1 || isNaN(repNum)) return null;
  const [d, m, y] = dateStr.split('-');
  const date = d && m && y ? new Date(`${y}-${m}-${d}T00:00:00Z`) : null;

  return {
    year: Number(year), repertoryNumber: repNum, date,
    month: date && !isNaN(date) ? date.getUTCMonth() + 1 : null,
    party1, party2, actType, docProtRef,
    createdAt: new Date(), updatedAt: new Date(),
  };
};

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────
const RepertoryIndexAdmin = () => {
  const [years, setYears] = useState([]);
  const [actTypes, setActTypes] = useState([]);
  const [importYear, setImportYear] = useState(null);
  const [parsedEntries, setParsedEntries] = useState([]);
  const [parseResult, setParseResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [refreshTable, setRefreshTable] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [gaps, setGaps] = useState(null);
  const [gapsYear, setGapsYear] = useState(null);
  const [loadingGaps, setLoadingGaps] = useState(false);

  const loadMeta = () => {
    getRepertoryYears().then(({ data }) => setYears(data.years || [])).catch(() => {});
    getRepertoryActTypes().then(({ data }) => setActTypes(data.actTypes || [])).catch(() => {});
  };

  useEffect(() => { loadMeta(); }, []);

  const handleFile = (file) => {
    if (!importYear) { message.warning('Selecciona el año primero'); return false; }
    const reader = new FileReader();
    reader.onload = (e) => {
      // Remover BOM UTF-8 si existe
      const text = e.target.result.replace(/^﻿/, '');
      const lines = text.split('\n').filter(l => l.trim());
      // Auto-detectar delimitador: si la primera línea tiene ; es CSV, sino TSV
      const delimiter = lines[0]?.includes(';') ? ';' : '\t';
      // Saltar encabezado si es CSV (primera línea contiene texto no numérico en col 3)
      const startIdx = (delimiter === ';' && isNaN(parseInt(lines[0]?.split(';')[2]))) ? 1 : 0;
      const entries = []; let skipped = 0;
      for (const line of lines.slice(startIdx)) {
        const entry = parseRow(line, importYear, delimiter);
        if (entry) entries.push(entry); else skipped++;
      }
      setParsedEntries(entries);
      setParseResult({ parsed: entries.length, skipped });
    };
    reader.readAsText(file, 'UTF-8');
    return false;
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      let total = 0;
      for (let i = 0; i < parsedEntries.length; i += 500) {
        const { data } = await importRepertoryIndex(parsedEntries.slice(i, i + 500));
        total += data.imported || 0;
      }
      message.success(`${total} registros importados correctamente`);
      setParsedEntries([]); setParseResult(null);
      loadMeta(); setRefreshTable(r => r + 1);
    } catch (e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message || e?.response?.data?.error;
      const detail = e?.response?.data?.details || e?.response?.data?.errors;

      Modal.error({
        title: `Error al importar (${status || 'sin conexión'})`,
        content: (
          <div style={{ fontSize: 13 }}>
            <p style={{ marginBottom: 8 }}>
              {serverMsg || 'No se pudo completar la importación.'}
            </p>
            {detail && (
              <pre style={{
                background: '#fff2f0', border: '1px solid #ffccc7',
                borderRadius: 4, padding: 10, fontSize: 11,
                maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap',
              }}>
                {typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}
              </pre>
            )}
            <p style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
              Revisa que el archivo tenga el formato correcto y que el año esté bien seleccionado.
            </p>
          </div>
        ),
        okText: 'Entendido',
        width: 500,
      });
    }
    finally { setImporting(false); }
  };

  const handleExport = async (year) => {
    setExporting(true);
    try {
      const res = await exportRepertoryIndex(year);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = year ? `indice-repertorios-${year}.xlsx` : 'indice-repertorios.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { message.error('Error al exportar'); }
    finally { setExporting(false); }
  };

  const handleCheckGaps = async (year) => {
    if (!year) { message.warning('Selecciona un año para verificar huecos'); return; }
    setLoadingGaps(true);
    setGapsYear(year);
    try {
      const { data } = await getRepertoryGaps(year);
      setGaps(data);
    } catch { message.error('Error al verificar huecos'); }
    finally { setLoadingGaps(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#1d3557' }}>Índice de Repertorios</Title>
          <Text type="secondary">Gestiona el índice de escrituras públicas de la notaría.</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0f6ff', borderRadius: 8, padding: '6px 14px', border: '1px solid #d0e4ff' }}>
          <Text style={{ fontSize: 12, color: '#1d3557', fontWeight: 600 }}>{years.length} años cargados:</Text>
          {years.map(y => <Tag key={y} color="blue" style={{ margin: '0 2px' }}>{y}</Tag>)}
        </div>
      </div>

      {/* ── Alerta huecos ── */}
      {gaps && (
        <Card
          size="small"
          style={{ marginBottom: 16, borderRadius: 8, borderColor: gaps.total > 0 ? '#faad14' : '#52c41a' }}
          bodyStyle={{ padding: '12px 16px' }}
        >
          {gaps.total === 0 ? (
            <Text style={{ color: '#52c41a' }}>
              ✓ Sin huecos en el año {gapsYear} — repertorios del {gaps.min} al {gaps.max} completos ({gaps.count} registros).
            </Text>
          ) : (
            <div>
              <Text strong style={{ color: '#faad14' }}>
                <WarningOutlined /> {gaps.total} hueco{gaps.total !== 1 ? 's' : ''} detectado{gaps.total !== 1 ? 's' : ''} en el año {gapsYear}
              </Text>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                Repertorios faltantes: {gaps.gaps.slice(0, 50).join(', ')}{gaps.gaps.length > 50 ? ` ... y ${gaps.gaps.length - 50} más` : ''}
              </Text>
            </div>
          )}
        </Card>
      )}

      <Tabs defaultActiveKey="manual">

        {/* ── Ingreso manual ── */}
        <TabPane tab={<span><PlusOutlined /> Agregar manual</span>} key="manual">
          <Card style={{ borderRadius: 8 }}>
            <ManualEntryForm
              actTypes={actTypes}
              onCreated={() => { loadMeta(); setRefreshTable(r => r + 1); }}
            />
            <Divider style={{ margin: '20px 0 16px' }} />
            <RecentTable refresh={refreshTable} actTypes={actTypes} />
          </Card>
        </TabPane>

        {/* ── Importación masiva ── */}
        <TabPane tab={<span><InboxOutlined /> Importar TSV / CSV</span>} key="import">
          <Card style={{ borderRadius: 8 }}>
            <Alert type="info" showIcon style={{ marginBottom: 14 }}
              message={
                <div>
                  <strong>Formato TSV:</strong> OTORGANTES [tab] ACTO [tab] N°REP [tab] FECHA (DD-MM-AAAA) [tab] DOC PROT<br />
                  <strong>Formato CSV:</strong> <code>PARTE_1;PARTE_2;ACTO;N_REPERTORIO;FECHA;DOC_PROT</code> — cada otorgante en su propia columna.<br />
                  <strong>Formato TSV legacy:</strong> <code>OTORGANTES [tab] ACTO [tab] N°REP [tab] FECHA [tab] DOC_PROT</code> — dos partes separadas con <code>/</code>.
                </div>
              }
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <Space>
                <Text strong>Año:</Text>
                <Select placeholder="Año" value={importYear} onChange={setImportYear} style={{ width: 110 }}>
                  {[2021,2022,2023,2024,2025,2026,2027].map(y => <Option key={y} value={y}>{y}</Option>)}
                </Select>
              </Space>
              <Button icon={<DownloadOutlined />} onClick={downloadCsvTemplate} size="small">
                Descargar plantilla CSV
              </Button>
            </div>
            <Dragger accept=".txt,.tsv,.csv" beforeUpload={handleFile} showUploadList={false}
              disabled={!importYear} style={{ marginBottom: 12 }}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Arrastra o haz clic para seleccionar archivo TSV o CSV</p>
              <p className="ant-upload-hint">Selecciona el año antes de subir el archivo</p>
            </Dragger>
            {parseResult && (
              <Alert type={parseResult.parsed > 0 ? 'success' : 'warning'} style={{ marginBottom: 12 }}
                message={`${parseResult.parsed} registros válidos, ${parseResult.skipped} omitidos`} />
            )}
            {parsedEntries.length > 0 && (
              <Button type="primary" icon={<InboxOutlined />} loading={importing} onClick={handleImport}
                style={{ background: '#1d3557', borderColor: '#1d3557' }}>
                Importar {parsedEntries.length} registros
              </Button>
            )}
          </Card>
        </TabPane>

        {/* ── Herramientas ── */}
        <TabPane tab={<span><FileExcelOutlined /> Exportar / Verificar</span>} key="tools">
          <Card style={{ borderRadius: 8 }}>
            <Row gutter={[24, 24]}>
              {/* Export */}
              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>
                  <FileExcelOutlined style={{ color: '#217346', marginRight: 6 }} />
                  Exportar a Excel
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                  Descarga el índice completo o filtrado por año en formato .xlsx
                </Text>
                <Space wrap>
                  <Button
                    icon={<FileExcelOutlined />}
                    loading={exporting}
                    onClick={() => handleExport(null)}
                    style={{ color: '#217346', borderColor: '#217346' }}
                  >
                    Exportar todo
                  </Button>
                  {years.map(y => (
                    <Button
                      key={y}
                      size="small"
                      loading={exporting}
                      onClick={() => handleExport(y)}
                    >
                      {y}
                    </Button>
                  ))}
                </Space>
              </Col>

              {/* Gap checker */}
              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>
                  <WarningOutlined style={{ color: '#faad14', marginRight: 6 }} />
                  Verificar huecos en numeración
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                  Detecta repertorios faltantes dentro del rango de un año dado.
                </Text>
                <Space wrap>
                  {years.map(y => (
                    <Button
                      key={y}
                      size="small"
                      loading={loadingGaps && gapsYear === y}
                      icon={<WarningOutlined />}
                      onClick={() => handleCheckGaps(y)}
                    >
                      Verificar {y}
                    </Button>
                  ))}
                </Space>
                {gaps && gapsYear && (
                  <div style={{ marginTop: 14 }}>
                    {gaps.total === 0 ? (
                      <Tag color="success">✓ Sin huecos en {gapsYear} (rep. {gaps.min}–{gaps.max})</Tag>
                    ) : (
                      <div>
                        <Tag color="warning" icon={<WarningOutlined />}>
                          {gaps.total} hueco{gaps.total !== 1 ? 's' : ''} en {gapsYear}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                          {gaps.gaps.slice(0, 80).join(', ')}{gaps.gaps.length > 80 ? ` … +${gaps.gaps.length - 80} más` : ''}
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </TabPane>

      </Tabs>
    </div>
  );
};

export default RepertoryIndexAdmin;
